import type {
  CyberAssessmentAnswerPatchDto,
  CyberAssessmentAnswerSelectDto,
  CyberAssessmentCreateDto,
  CyberAssessmentEvidencePackChecklistItemPatchDto,
  CyberAssessmentEvidencePackChecklistSelectDto,
  CyberAssessmentSelectDto,
  CyberAssessmentsSelectDto,
} from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from './client';
import { queryKeys } from './keys';
import { useGetCurrentUser, useRoleEndpointPrefix } from './users.query';

export const getTechnologyCyberAssessmentsQueryOptions = (params: {
  apiGet: ReturnType<typeof useApi>['apiGet'];
  enabled: boolean;
  roleEndpointPrefix?: string;
  technologyId?: string;
}) => {
  const { apiGet, enabled, roleEndpointPrefix, technologyId } = params;

  return queryOptions<CyberAssessmentSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const result = await apiGet<CyberAssessmentsSelectDto>({
        path: `/v1/${roleEndpointPrefix}/technologies/${technologyId}/cyber-assessments`,
      });

      return result.cyberAssessments;
    },
    queryKey: technologyId
      ? queryKeys.technology.getTechnologyCyberAssessments(technologyId)
      : queryKeys.placeholders.default,
  });
};

export const useGetTechnologyCyberAssessments = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isExpert, isInnovator, isIntel } = useGetCurrentUser();
  const roleEndpointPrefix = useRoleEndpointPrefix();
  const enabled = Boolean(technologyId && roleEndpointPrefix) && (isExpert || isInnovator || isIntel);
  const queryOptions = getTechnologyCyberAssessmentsQueryOptions({
    apiGet,
    enabled,
    roleEndpointPrefix: roleEndpointPrefix ?? undefined,
    technologyId,
  });

  return useQuery(queryOptions);
};

export const useGetCyberAssessment = (cyberAssessmentId?: string | null) => {
  const { apiGet } = useApi();
  const { isExpert, isInnovator, isIntel } = useGetCurrentUser();
  const roleEndpointPrefix = useRoleEndpointPrefix();
  const enabled = Boolean(cyberAssessmentId && roleEndpointPrefix) && (isExpert || isInnovator || isIntel);

  return useQuery<CyberAssessmentSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<CyberAssessmentSelectDto>({
        path: `/v1/${roleEndpointPrefix}/cyber-assessments/${cyberAssessmentId}`,
      });
    },
    queryKey: cyberAssessmentId
      ? queryKeys.technology.getCyberAssessment(cyberAssessmentId)
      : queryKeys.placeholders.default,
  });
};

export const useCreateCyberAssessment = (technologyId?: string) => {
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();

  return useMutation<CyberAssessmentSelectDto, ApiErrorWrapper, CyberAssessmentCreateDto>({
    mutationFn: async (createDto: CyberAssessmentCreateDto) => {
      if (!isInnovator) {
        throw new ApiErrorWrapper({
          errors: [{ message: 'Only Innovators can create Cyber Assessments.', statusCode: 403 }],
        });
      }
      if (!technologyId) {
        throw new ApiErrorWrapper({
          errors: [{ message: 'Technology ID is required to create a Cyber Assessment.', statusCode: 400 }],
        });
      }

      return await apiPost<CyberAssessmentSelectDto>({
        body: createDto,
        path: `/v1/innovator/technologies/${technologyId}/cyber-assessments`,
      });
    },
    onSuccess: (data: CyberAssessmentSelectDto) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyCyberAssessments(data.technologyId),
      });
    },
  });
};

export const useDeleteCyberAssessment = () => {
  const { apiDelete } = useApi();
  const { isInnovator } = useGetCurrentUser();

  return useMutation<CyberAssessmentSelectDto, ApiErrorWrapper, { cyberAssessmentId: string; technologyId: string }>({
    mutationFn: async ({ cyberAssessmentId }) => {
      if (!isInnovator) {
        throw new ApiErrorWrapper({
          errors: [{ message: 'Only Innovators can delete Cyber Assessments.', statusCode: 403 }],
        });
      }

      return await apiDelete<CyberAssessmentSelectDto>({
        path: `/v1/innovator/cyber-assessments/${cyberAssessmentId}`,
      });
    },
    onSuccess: (cyberAssessment, { technologyId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyCyberAssessments(technologyId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.technology.getCyberAssessment(cyberAssessment.id),
      });
    },
  });
};

export const useInnovatorPatchCyberAssessmentAnswer = () => {
  const { apiPatch } = useApi();
  const { isInnovator } = useGetCurrentUser();

  return useMutation<CyberAssessmentAnswerSelectDto, ApiErrorWrapper, CyberAssessmentAnswerPatchDto>({
    mutationFn: async (patchDto: CyberAssessmentAnswerPatchDto) => {
      if (!isInnovator) {
        throw new ApiErrorWrapper({
          errors: [{ message: 'Only Innovators can update Cyber Assessment answers.', statusCode: 403 }],
        });
      }

      return await apiPatch<CyberAssessmentAnswerSelectDto>({
        body: patchDto,
        path: '/v1/innovator/cyber-assessment-answers',
      });
    },
    onSuccess: (answer) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getCyberAssessment(answer.cyberAssessmentId),
      });
    },
  });
};

export const usePatchCyberAssessmentEvidencePackChecklistItem = () => {
  const { apiPatch } = useApi();
  const { isInnovator } = useGetCurrentUser();

  return useMutation<
    CyberAssessmentEvidencePackChecklistSelectDto,
    ApiErrorWrapper,
    CyberAssessmentEvidencePackChecklistItemPatchDto & { cyberAssessmentId: string }
  >({
    mutationFn: async ({ cyberAssessmentId, ...patchDto }) => {
      if (!isInnovator) {
        throw new ApiErrorWrapper({
          errors: [{ message: 'Only Innovators can update Cyber Assessment evidence packs.', statusCode: 403 }],
        });
      }

      return await apiPatch<CyberAssessmentEvidencePackChecklistSelectDto>({
        body: patchDto,
        path: `/v1/innovator/cyber-assessments/${cyberAssessmentId}/evidence-pack-checklist`,
      });
    },
    onSuccess: (_checklist, { cyberAssessmentId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getCyberAssessment(cyberAssessmentId),
      });
    },
  });
};

export const useCompleteCyberAssessment = (cyberAssessmentId?: string | null) => {
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();

  return useMutation<CyberAssessmentSelectDto, ApiErrorWrapper, void>({
    mutationFn: async () => {
      if (!isInnovator) {
        throw new ApiErrorWrapper({
          errors: [{ message: 'Only Innovators can complete Cyber Assessments.', statusCode: 403 }],
        });
      }

      if (!cyberAssessmentId) {
        throw new ApiErrorWrapper({
          errors: [{ message: 'Cyber Assessment ID is required.', statusCode: 400 }],
        });
      }

      return await apiPost<CyberAssessmentSelectDto>({
        path: `/v1/innovator/cyber-assessments/${cyberAssessmentId}/complete`,
      });
    },
    onSuccess: (cyberAssessment) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getCyberAssessment(cyberAssessment.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyCyberAssessments(cyberAssessment.technologyId),
      });
    },
  });
};
